import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  StarIcon,
  MinusCircleIcon,
  TrashIcon,
  Bars3Icon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { SpotImage } from "@/common/model/spotimages/SpotImage";

interface SortableImageCardProps {
  image: SpotImage;
  isCover: boolean;
  onSetCover: (filename: string) => void;
  onRemove: (filename: string) => void;
  onDelete: (filename: string) => void;
  onClick: () => void;
}

const SortableImageCard: React.FC<SortableImageCardProps> = ({
  image,
  isCover,
  onSetCover,
  onRemove,
  onDelete,
  onClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.filename });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleButtonClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group bg-white rounded-lg shadow-md overflow-hidden cursor-pointer ${
        isCover ? "ring-2 ring-yellow-500" : ""
      } ${isDragging ? "z-50" : ""}`}
      onClick={onClick}
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 z-10 p-1 bg-black/50 rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <Bars3Icon className="h-4 w-4 text-white" />
      </div>

      {/* Cover badge */}
      {isCover && (
        <div className="absolute top-2 right-2 z-10 px-2 py-1 bg-yellow-500 text-white text-xs font-semibold rounded flex items-center gap-1">
          <StarIconSolid className="h-3 w-3" />
          Cover
        </div>
      )}

      {/* Score badge */}
      <div className="absolute bottom-2 left-2 z-10 px-2 py-1 bg-black/50 text-white text-xs rounded">
        {image.score !== null ? (
          `${(image.score * 100).toFixed(1)}%`
        ) : (
          <span className="text-gray-300">No score</span>
        )}
      </div>

      {/* Image */}
      <div className="aspect-video">
        <img
          src={image.publicUrl}
          alt={image.filename}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>

      {/* Action buttons overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
        {!isCover && (
          <button
            onClick={(e) => handleButtonClick(e, () => onSetCover(image.filename))}
            className="p-2 bg-white rounded-full shadow hover:bg-yellow-50 transition-colors"
            title="Set as cover"
          >
            <StarIcon className="h-5 w-5 text-yellow-500" />
          </button>
        )}
        {isCover && (
          <div className="p-2 bg-yellow-500 rounded-full shadow">
            <StarIconSolid className="h-5 w-5 text-white" />
          </div>
        )}
        <button
          onClick={(e) => handleButtonClick(e, () => onRemove(image.filename))}
          className="p-2 bg-white rounded-full shadow hover:bg-orange-50 transition-colors"
          title="Remove from selection"
        >
          <MinusCircleIcon className="h-5 w-5 text-orange-500" />
        </button>
        <button
          onClick={(e) => handleButtonClick(e, () => onDelete(image.filename))}
          className="p-2 bg-white rounded-full shadow hover:bg-red-50 transition-colors"
          title="Delete permanently"
        >
          <TrashIcon className="h-5 w-5 text-red-500" />
        </button>
      </div>
    </div>
  );
};

export default SortableImageCard;
